/**
 * Multi-simulation setup for the Run Simulation modal: each sim has its own
 * ride layer and fleet, and overrides only the parameters that differ from base.
 */

/** Parameters a single sim can override. Everything else is shared from base. */
export interface SimParams {
  timeWindowSlope: number;
  timeWindowIntercept: number;
  timeWindowFixedWindow: number;
  travelTimeMultiplier: number;
  maxTripTimeMinutes: number;
  iterationCount: number;
  clusteringEnabled: boolean;
}

export interface SimSetup {
  key: string;
  /** Appended to the run name, e.g. "Peak test · A" */
  suffix: string;
  rideLayerId: string;
  fleetId: string;
  overrides: Partial<SimParams>;
}

export const MIN_SIMS = 2;
export const MAX_SIMS = 4;
const SUFFIXES = ['A', 'B', 'C', 'D'];

export const effectiveParams = (base: SimParams, overrides: Partial<SimParams>): SimParams => ({
  ...base,
  ...overrides,
});

/**
 * Fields this sim pins. A stored override stays marked even if Base later reaches the same value,
 * because it no longer follows Base: changing Base again would leave this sim behind.
 */
export const changedOverrideKeys = (_base: SimParams, overrides: Partial<SimParams>): (keyof SimParams)[] =>
  Object.keys(overrides) as (keyof SimParams)[];

/** Sets an override, dropping it when the value matches base so the sim follows base again. */
export const withOverride = <K extends keyof SimParams>(
  base: SimParams,
  overrides: Partial<SimParams>,
  key: K,
  value: SimParams[K],
): Partial<SimParams> => {
  const { [key]: _dropped, ...rest } = overrides;
  return value === base[key] ? rest : { ...rest, [key]: value };
};

/** First letter not already used, so removing Sim B and adding again gives B back. */
export const nextSuffix = (sims: SimSetup[]): string =>
  // Add is only offered below MAX_SIMS, so a letter is always free
  SUFFIXES.find(s => !sims.some(sim => sim.suffix === s))!;

/** A new sim on the same rides and fleet as `from` (usually the last card). */
export const newSim = (sims: SimSetup[], from: Pick<SimSetup, 'rideLayerId' | 'fleetId'>): SimSetup => {
  const suffix = nextSuffix(sims);
  return { key: `sim-${suffix}-${Date.now()}`, suffix, rideLayerId: from.rideLayerId, fleetId: from.fleetId, overrides: {} };
};

export const simRunName = (runName: string, suffix: string): string =>
  suffix.trim() ? `${runName.trim()} · ${suffix.trim()}` : runName.trim();
